import { useDialog } from "../../../../Upsell/modernTS/JUI/Dialogs";
import React, { useEffect, useState } from "react";
import { BlockUserInput } from "@jamezz/react-components";
import { read, utils } from "xlsx";
import _ from "lodash";
import DialogContent from "@mui/material/DialogContent";
import {
  MenuItem,
  Paper,
  Select,
  Stack,
  styled,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from "@mui/material";
import Table from "@mui/material/Table/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import { ArrowForward, CloudUpload } from "@mui/icons-material";
import DialogActions from "@mui/material/DialogActions";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function UploadExcel({
  columns,
  onDataAccept,
}: {
  columns: { value: string; label: string }[];
  onDataAccept: (data: string[][]) => void;
}) {
  const { closeDialog } = useDialog();
  const [file, setFile] = useState();
  const [workbook, setWorkbook] = useState();
  const { blockingStopped, blockingStarted } = BlockUserInput.useBlockUserInput();
  const [selectedSheetName, setSelectedSheetName] = useState();
  const [selectedSheet, setSelectedSheet] = useState();
  const [firstRowWithData, setFirstRowWithData] = useState(1);
  const [numberOfColumns, setNumberOfColumns] = useState(0);

  const [columnsMapping, setColumnsMapping] = useState<number[]>();

  useEffect(() => {
    if (columns) {
      setColumnsMapping(columns.map((column, index) => index));
    }
  }, [columns]);

  useEffect(() => {
    if (file) {
      blockingStarted();
      const fr = new FileReader();

      fr.onload = function (event) {
        const workbook = read(event.target.result, { dense: true, type: "string" });
        setWorkbook(workbook);
        blockingStopped();
      };
      fr.readAsArrayBuffer(file);
    }
  }, [blockingStarted, blockingStopped, file]);
  useEffect(() => {
    if (workbook) {
      setSelectedSheetName(workbook.SheetNames[0]);
    }
  }, [workbook]);
  useEffect(() => {
    if (selectedSheetName) {
      const sheet = utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { header: 1 });
      const columns: { [columnIndex: number]: true } = {};
      sheet.forEach((row) => {
        row.forEach((cell, columnIndex) => {
          if (cell != null) {
            columns[columnIndex] = true;
          }
        });
      });
      const maxColumnIndex = _.chain(columns).keys().max().value();
      setNumberOfColumns(Number(maxColumnIndex) + 2);
      setSelectedSheet(sheet.map((row) => row.slice(0, maxColumnIndex + 1)));
    }
  }, [selectedSheetName, workbook?.SheetNames, workbook?.Sheets]);

  const MAX_ROWS = 10;

  return (
    <>
      <DialogContent>
        {selectedSheet ? (
          <Stack alignItems={"center"} direction={"row"} gap={4} sx={{ p: 4 }}>
            <Paper sx={{ width: 0, flex: "1 1 0%" }}>
              <Toolbar>
                <Typography sx={{ fontWeight: 800 }}>Input</Typography>
              </Toolbar>
              <TableContainer>
                <Table size={"small"}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>#</TableCell>
                      {_.times(numberOfColumns, _.constant(null)).map((_, index) => (
                        <TableCell key={index} sx={{ fontWeight: 800 }}>
                          Column {index + 1}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedSheet.slice(0, MAX_ROWS).map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {index + 1}
                          {index < 2 ? (
                            <Button
                              disabled={index == firstRowWithData}
                              onClick={() => {
                                setFirstRowWithData(index);
                              }}
                              variant={"contained"}
                            >
                              set
                            </Button>
                          ) : null}
                        </TableCell>
                        {row.map((cell, index2) => (
                          <TableCell key={index2}>{cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}

                    {selectedSheet.length > MAX_ROWS ? (
                      <TableRow>
                        <TableCell />
                        <TableCell>
                          etc another {selectedSheet?.slice(firstRowWithData).length - MAX_ROWS} rows
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <ArrowForward />
            <Paper sx={{ width: 0, flex: "1 1 0%" }}>
              <Toolbar>
                <Typography sx={{ fontWeight: 800 }}>Output</Typography>
              </Toolbar>
              <TableContainer component={Paper}>
                <Table size={"small"}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>#</TableCell>
                      {columns.map((column, index) => (
                        <TableCell key={index} sx={{ fontWeight: 800 }}>
                          <Stack alignItems={"center"} direction={"row"} gap={2}>
                            {column.label}:
                            {columnsMapping?.[index] != null && selectedSheet ? (
                              <Select
                                onChange={(e) => {
                                  const newColumnsMapping = [...columnsMapping];
                                  newColumnsMapping[index] = e.target.value;
                                  setColumnsMapping(newColumnsMapping);
                                }}
                                value={String(columnsMapping?.[index])}
                              >
                                {_.times(numberOfColumns, _.constant(null)).map((_, index) => (
                                  <MenuItem key={index} value={String(index)}>
                                    Column {index + 1}
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : null}
                          </Stack>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedSheet
                      ?.slice(firstRowWithData)
                      .slice(0, MAX_ROWS)
                      .filter((row) => !columns.some((column, index) => row[index] == null))
                      .map((row, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontWeight: 800 }}>{index + 1}</TableCell>
                          {columns.map((column, index) => (
                            <TableCell key={index}>{row[columnsMapping[index]]}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    {selectedSheet?.slice(firstRowWithData).length > MAX_ROWS ? (
                      <TableRow>
                        <TableCell />
                        <TableCell>
                          etc another {selectedSheet?.slice(firstRowWithData).length - MAX_ROWS} rows
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        ) : (
          <Typography sx={{ width: 1, textAlign: "center", fontWeight: 800 }}>
            Please upload an excel or csv file
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button component={"label"} startIcon={<CloudUpload />} tabIndex={-1} variant={"contained"}>
          Upload file
          <VisuallyHiddenInput id={"UploadExcel-File"} onChange={(e) => setFile(e.target.files[0])} type={"file"} />
        </Button>
        <Button onClick={() => closeDialog("upload-excel")} variant={"contained"}>
          Cancel
        </Button>
        <Button
          disabled={selectedSheet == null}
          onClick={() => {
            const data = selectedSheet
              ?.slice(firstRowWithData)
              .filter((row) => !columns.some((column, index) => row[index] == null))
              .map((row) => columns.map((column, index) => row[columnsMapping[index]]));
            onDataAccept(data);
            closeDialog("upload-excel");
          }}
          variant={"contained"}
        >
          Accept
        </Button>
      </DialogActions>
    </>
  );
}
